/**
 * Copyright (c) 2022 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import { Text, Typography } from '@blend/enterprise-components';
import { css, StyleSheet } from 'aphrodite';
import React from 'react';
import { useSelector } from 'react-redux';
import { DateTime } from 'luxon';
import { State } from '../../../../redux';
import { enterpriseColors } from '../../../../style/colors';
import { formatIsoDate, formatTimeSlotList } from '@utils/formatting';

const styles = StyleSheet.create({
  wrapper: {
    ...Typography.Roboto(),
    color: enterpriseColors.NEUTRAL_800,
    padding: '24px 24px',
    maxWidth: 600,
    border: `1px solid ${enterpriseColors.NEUTRAL_200}`,
    borderRadius: 6,
    backgroundColor: enterpriseColors.WHITE,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'start',
  },
  messageWrapper: {
    display: 'inline-block',
    padding: '4px 8px',
    fontSize: 14,
    marginTop: 8,
  },
  sharedPreferenceMessage: {
    borderLeft: `4px solid ${enterpriseColors.NEUTRAL_300}`,
    color: enterpriseColors.NEUTRAL_700,
    backgroundColor: enterpriseColors.NEUTRAL_75,
  },
  haveNotSharedPreferenceMessage: {
    borderLeft: `4px solid ${enterpriseColors.ALERT_500}`,
    color: enterpriseColors.ALERT_900,
    backgroundColor: enterpriseColors.ALERT_25,
  },
  timeHeader: {
    fontSize: 11,
    fontWeight: 500,
  },
});

export const SchedulePreferencesCard: React.FC = ({}) => {
  const roomDetail = useSelector((reduxState: State) => reduxState.roomDetail);
  const scheduleDate: string = roomDetail.room?.scheduleDate;
  const preferredTimeSlots: string[] = roomDetail.room?.preferredTimeSlots;
  const preferredTimeZone: string = roomDetail.room?.preferredTimeZone;

  // Eclose will send an empty list of preferred time slots to indicate that
  // a borrower does not have the scheduling feature enabled
  // If the preferred time slots is null, the borrower has scheduling enabled
  // and has not updated their preferences yet
  const isSchedulingEnabledAndPreferencesProvided = preferredTimeSlots?.length > 0 && !!preferredTimeZone;
  const isSchedulingDisabled = preferredTimeSlots?.length === 0;

  const borrowerTimeZone = DateTime.fromISO(scheduleDate, { zone: preferredTimeZone });
  const unAbbreviatedTimeZoneFormat = borrowerTimeZone.toFormat('ZZZZZ');
  const abbreviatedTimeZoneFormat = borrowerTimeZone.toFormat('ZZZZ');

  const notSharedTimeSlotsMessage = isSchedulingEnabledAndPreferencesProvided ? (
    <>
      <div
        data-it="schedulePreferenceCard-prefs"
        className={css(styles.messageWrapper, styles.sharedPreferenceMessage)}
      >
        <Text>
          Borrower prefers {formatIsoDate(scheduleDate)} from{' '}
          {formatTimeSlotList(scheduleDate, preferredTimeSlots, preferredTimeZone)}
        </Text>
      </div>
      <div
        data-it="schedulePreferenceCard-timezone"
        className={css(styles.messageWrapper, styles.sharedPreferenceMessage)}
      >
        <Text>
          Borrower's selected time zone is {unAbbreviatedTimeZoneFormat} ({abbreviatedTimeZoneFormat})
        </Text>
      </div>
    </>
  ) : (
    <div
      data-it="schedulePreferenceCard-prefs"
      className={css(styles.messageWrapper, styles.haveNotSharedPreferenceMessage)}
    >
      {isSchedulingDisabled ? (
        <Text>Scheduling is not enabled for this customer</Text>
      ) : (
        <Text>Borrower has not shared their preferences</Text>
      )}
    </div>
  );

  return (
    <div className={css(styles.wrapper)} data-it="schedulePreferenceCard">
      <Text fontSize="xs" weight="semibold">
        TIME
      </Text>
      {notSharedTimeSlotsMessage}
    </div>
  );
};
